import Dropzone from "react-dropzone";
import { usePartnerRequestsStore2 } from "../../../../stores/Cac/Partner/partnerRequests";
import { handleUploadFiles } from "../../../../services/Api/requestService";
import {
  showToastError,
  showToastSuccess,
} from "../../../../services/Toasts/toasts";
import { useState } from "react";
import { SECURITY_CONFIG_UPLOAD, isFileAllowed, detectAndFixFileMimeType } from "../../../../services/Utils/security";

export const DropZoneDocument = ({
  requestId,
  compact = false,
}: {
  requestId: number;
  compact?: boolean;
}) => {
  const { updateRequest, setUpdateRequest } = usePartnerRequestsStore2();
  const [isDragActive, setIsDragActive] = useState(false);

  const handleUploadClick = async (files: File[]) => {
    // Validation de base
    if (files.length > SECURITY_CONFIG_UPLOAD.maxFiles) {
      showToastError(`Maximum ${SECURITY_CONFIG_UPLOAD.maxFiles} fichiers autorisés`);
      return;
    }
    if (files.some((file) => file.size > SECURITY_CONFIG_UPLOAD.maxFileSize)) {
      showToastError(`Fichier trop volumineux`);
      return;
    }
    if (files.some((file) => !isFileAllowed(file))) {
      showToastError(`Type de fichier non autorisé`);
      return;
    }
    if (files.some((file) => file.name.length > SECURITY_CONFIG_UPLOAD.nameMaxLength)) {
      showToastError(`Nom de fichier trop long`);
      return;
    }

    try {
      // Détecter et corriger les types MIME de tous les fichiers
      const fixedFiles = await Promise.all(
        files.map(file => detectAndFixFileMimeType(file))
      );

      // Envoyer les fichiers avec les types MIME corrects
      await handleUploadFiles(requestId, fixedFiles);
      setUpdateRequest(!updateRequest);
      showToastSuccess("Fichier téléchargé avec succès");
    } catch (error) {
      showToastError(
        "Format accepté : csv, doc, docx, jpeg, png, pdf, txt, xls, xlsx, xlsm, zip"
      );
    }
  };

  const handleDrop = (acceptedFiles: File[]) => {
    handleUploadClick(acceptedFiles);
  };

  return (
    <Dropzone 
      onDrop={handleDrop} 
      multiple={true}
      noClick={false}
      noKeyboard={false}
      onDragEnter={() => setIsDragActive(true)}
      onDragLeave={() => setIsDragActive(false)}
      onDropAccepted={() => setIsDragActive(false)}
      onDropRejected={() => setIsDragActive(false)}
    >
      {({ getRootProps, getInputProps, isDragReject }) => (
        <div
          {...getRootProps()}
          className={`
            relative w-full max-w-2xl mx-auto rounded-2xl border-2 border-dashed cursor-pointer
            ${compact ? "mb-2 p-2" : "mb-3 p-2"}
            transition-all duration-300 ease-in-out transform
            ${isDragActive 
              ? 'border-blue-500 bg-blue-50 shadow-lg sm:scale-105' 
              : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
            }
            ${isDragReject ? 'border-red-400 bg-red-50' : ''}
            group
          `}
        >
          <input {...getInputProps()} />
          
          {/* Icône principale */}
          <div
            className={`flex justify-center ${
              compact ? "mb-1" : "mb-1 sm:mb-4"
            }`}
          >
            <div className={`
              rounded-full p-2 transition-all duration-300
              ${compact ? "" : "sm:p-4"}
              ${isDragActive 
                ? 'bg-blue-100 text-blue-600 scale-110' 
                : 'bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-600'
              }
            `}>
              <svg
                className={`transition-transform duration-300 group-hover:rotate-12 ${
                  compact ? "h-6 w-6" : "h-6 w-6 sm:h-10 sm:w-10"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
          </div>

          {/* Texte principal */}
          <div
            className={`text-center ${
              compact ? "mb-0" : "mb-1 sm:mb-4"
            }`}
          >
            <h3 className={`
              mb-1 text-sm font-semibold transition-colors duration-300
              ${compact ? "sm:text-base" : "sm:mb-2 sm:text-xl"}
              ${isDragActive ? 'text-blue-700' : 'text-gray-700'}
            `}>
              {isDragActive ? 'Déposez vos fichiers ici' : 'Glissez et déposez vos fichiers'}
            </h3>
            <p className={`
              text-sm transition-colors duration-300
              ${compact ? "hidden lg:block" : "hidden sm:block"}
              ${isDragActive ? 'text-blue-600' : 'text-gray-500'}
            `}>
              ou cliquez pour parcourir vos fichiers
            </p>
          </div>

          {/* Indicateur de drag & drop */}
          {isDragActive && (
            <div className="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-2xl border-2 border-blue-500 border-dashed flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-blue-700 font-medium">Relâchez pour télécharger</p>
              </div>
            </div>
          )}

          {/* Message d'erreur si type non supporté */}
          {isDragReject && (
            <div className="absolute inset-0 bg-red-500 bg-opacity-10 rounded-2xl border-2 border-red-400 border-dashed flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="text-red-700 font-medium">Type de fichier non supporté</p>
              </div>
            </div>
          )}

          {/* Effet de brillance au survol */}
          <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </div>
        </div>
      )}
    </Dropzone>
  );
};